import {useCallback, useEffect, useState} from 'react';
import {GridCellParams, GridRenderCellParams} from '@mui/x-data-grid';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {t} from 'i18next';
import {ReactComponent as CloseIcon} from '../../../../assests/svg/close_icon.svg';
import AurButton from '../../../common/aur-button/AurButton';
import {AurDataGridColDef} from '../../../../types/types';
import AurCheckbox from '../../../common/aur-checkbox/AurCheckbox';
import AurTreeDataGridPro from '../../../common/aur-tree-data-grid-pro/AurTreeDataGridPro';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {getRelationalGroups} from '../../../../api/EcaServices';
import {failedToLoadStatusCodes} from '../../../../constants/constants';
import {setRelationalGroupFields} from '../../../../redux/slices/strSlice';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: string[]) => void;
  selectedFields: string[];
  disablePortal?: boolean; // Use it for jest test case only
}

export type RelationalGroupFieldType = {
  key: string;
  name: string;
  id: string;
};

const stringPath = 'eca.searchTermReport.resultSetting.relationalGroupModal.';
const FamilyRelationalGroup = 'FamilyMD5Hash';
const GroupIdentifier = 'GroupIdentifier';

const RelationalGroupFieldsSelectionDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  selectedFields,
  disablePortal,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedGroupIds, setSelectedGroupIds] =
    useState<string[]>(selectedFields);
  const [groupFieldData, setGroupFieldData] = useState<
    RelationalGroupFieldType[]
  >([]);
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {trackGAEvent} = useAnalytics();

  const {projectId} = useSelector((state: RootState) => state.reports);
  const [haveReqError, setHaveReqError] = useState<boolean>(false);
  const [reqLoading, setReqLoading] = useState<boolean>(false);

  useEffect(() => {
    setSelectedGroupIds(selectedFields);
  }, [selectedFields]);

  const fetchRelationalGroups = async () => {
    setReqLoading(true);
    try {
      const path = `?clientId=${clientId}&projectId=${projectId}`;
      const response = await getRelationalGroups(path);
      dispatch(setRelationalGroupFields(response?.data.data ?? []));
      setGroupFieldData(response?.data.data ?? []);
    } catch (error: unknown) {
      if (
        failedToLoadStatusCodes.includes((error as {status: number}).status)
      ) {
        setHaveReqError(true);
      }
    } finally {
      setReqLoading(false);
    }
  };
  useEffect(() => {
    if (isOpen) {
      fetchRelationalGroups();
    }
  }, [isOpen]);

  const handleCheckboxCellClick = useCallback(
    (params: GridCellParams) => {
      const {id} = params;
      let selectedIds: string[] = [...selectedGroupIds];
      if (selectedIds.includes(id.toString())) {
        selectedIds = selectedIds.filter((item) => item !== id.toString());
      } else {
        selectedIds = [...selectedIds, id.toString()];
      }
      setSelectedGroupIds([...selectedIds]);
    },
    [selectedGroupIds]
  );

  const renderIdCell = (params: GridRenderCellParams) => {
    const {
      row: {id},
    } = params;

    const rowIndex = params.api.getAllRowIds().indexOf(id);
    const isChecked = selectedGroupIds.includes(id.toString());

    return (
      <Box className="id-checkbox-cell">
        <Typography className="typo-text body3_size">{rowIndex + 1}</Typography>
        <AurCheckbox
          checked={isChecked}
          onChange={handleCheckboxCellClick.bind(this, params)}
          data-testid={`relationship-data-${id}`}
          inputProps={{
            'aria-label': params.row.name,
          }}
        />
      </Box>
    );
  };

  const tableColumns: AurDataGridColDef[] = [
    {
      field: 'index',
      headerName: t('common.gridColumnHeader'),
      flex: 0.09,
      sortable: false,
      filterable: false,
      resizable: false,
      renderCell: renderIdCell,
      minWidth: 96,
      renderHeader: () => {
       return (
        <>
          <span aria-hidden="true">#</span>
          <span className="sr-only">
            {t('common.gridColumnHeader')}
          </span>
        </>
        );
      },
      renderHeaderFilter: () => (<span className='sr-only'>{t('common.checkboxColumnFilter')}</span>)
    },
    {
      field: 'name',
      headerName: t(stringPath + 'friendlyName'),
      flex: 0.91,
      sortable: false,
      resizable: false,
      headerClassName: 'friendlyName-header',
    },
  ];

  const handleSaveClick = () => {
    onSave(selectedGroupIds);

    const name: any = {};
    groupFieldData.map((item) => {
      if (selectedGroupIds.includes(`${item.id}`)) {
        const hasFamilyRelationalGroup = item.name.includes(
          FamilyRelationalGroup
        );
        const hasGroupIdentifier = item.name.includes(GroupIdentifier);
        if (hasFamilyRelationalGroup) {
          name[FamilyRelationalGroup] = true;
        } else if (hasGroupIdentifier) {
          name[GroupIdentifier] = true;
        } else {
          name['custom'] = true;
        }
      }
    });

    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.save'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.saveRelationalGroup'),
        details: t('gaTracking.str.relationalGroupValues', {
          relationalValues: Object.keys(name).join(' and '),
        }),
      },
      subPage: {
        subpage_name: t('gaTracking.str.relationalGroupModal'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };
  const handleRefreshClick = () => {
    fetchRelationalGroups();
  };
  const handleOnClose = () => {
    setSelectedGroupIds([]);
    onClose();
  };
  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleOnClose}
      maxWidth={false}
      disablePortal={disablePortal}
      PaperProps={{className: 'str-relational-group-fields-selection-dialog'}}
      data-testid="relational-group-field-dialog"
      aria-labelledby='relational-group-dialog-title'
    >
      <DialogContent className="modal-content">
        <>
          <div className="relational-dialog-title-row">
            <Typography
              className="typo-text headline2_size title-label"
              component="h2"
              id="relational-group-dialog-title"
            >
              {t(stringPath + 'title')}
            </Typography>
            <IconButton onClick={handleOnClose} data-testid="close-icon-button" aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className="group-list-table-container">
            <AurTreeDataGridPro
              treeData={false}
              rows={groupFieldData}
              headerFilters
              columns={tableColumns}
              getRowId={(row) => row.id}
              rowHeight={56}
              columnHeaderHeight={64}
              headerFilterHeight={64}
              className="aur-data-grid"
              onCellClick={handleCheckboxCellClick}
              rowSelection={false}
              hideFooter
              noRowOverlayClass="no-row-container"
              data-testid="group-field-list-table"
              isReqLoading={reqLoading}
              haveReqError={haveReqError}
              onErrorRefresh={handleRefreshClick}
              scrollbarSize={0}
            />
          </div>
        </>
      </DialogContent>
      <DialogActions className="footer-action-container">
        <AurButton
          type="exit"
          onClick={handleOnClose}
          tabIndex={0}
          dataTestId="dialog-cancel-button"
          label={t('buttons.cancel')}
          size="Medium"
        />
        <AurButton
          classNames="button-save"
          type="continue"
          onClick={handleSaveClick}
          tabIndex={0}
          dataTestId="dialog-save-button"
          label={t('buttons.save')}
          size="Medium"
          disabled={selectedGroupIds.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
};

export default RelationalGroupFieldsSelectionDialog;
